import { useEffect, useRef, useState } from 'react';
import type { HotelPublicContent, HotelContentLanguage } from '@bandido/shared/types/hotels/hotel-content';
import type { HotelSearchTransport } from './hotelSearchFlow';
/** Published content uses exact language matching. Regional UI locales select their base language explicitly. */
export function hotelContentLanguage(locale: string): HotelContentLanguage {
  const base = locale.split(/[-_]/)[0].toLowerCase();
  const language = /^[a-z]{1,16}$/.test(base) ? base : 'en';
  return { language, ...(language !== 'en' ? { fallback_language: 'en' } : {}) };
}
export async function readHotelContentBatch(
  transport: HotelSearchTransport,
  ids: string[],
  language: HotelContentLanguage,
  signal: AbortSignal,
) {
  const unique = [...new Set(ids)].sort();
  if (unique.length > 100) throw new Error('Content page too large');
  if (!unique.length) return {};
  if (signal.aborted) throw new Error('Content read aborted');
  const response = await transport.contentSummaries(unique, language, signal);
  if (signal.aborted) throw new Error('Content read aborted');
  if (response.data.some((d) => !unique.includes(d.hotel_id))) throw new Error('Content identity mismatch');
  return Object.fromEntries(response.data.map((d) => [d.hotel_id, d]));
}
function usePublishedContent<T>(key: string, read: (signal: AbortSignal) => Promise<T>, empty: T) {
  const [state, setState] = useState<{ key: string; data: T; error: boolean; loading: boolean }>({
    key,
    data: empty,
    error: false,
    loading: true,
  });
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0),
    activeKey = useRef(key),
    reader = useRef(read);
  activeKey.current = key;
  reader.current = read;
  useEffect(() => {
    const id = ++generation.current,
      abort = new AbortController();
    const current = () => id === generation.current && activeKey.current === key;
    setState({ key, data: empty, error: false, loading: true });
    const timer = setTimeout(() => {
      abort.abort();
      if (current()) setState({ key, data: empty, error: true, loading: false });
    }, 15000);
    void reader
      .current(abort.signal)
      .then((data) => {
        if (current() && !abort.signal.aborted) setState({ key, data, error: false, loading: false });
      })
      .catch(() => {
        if (current()) setState({ key, data: empty, error: true, loading: false });
      })
      .finally(() => clearTimeout(timer));
    return () => {
      generation.current++;
      abort.abort();
      clearTimeout(timer);
    };
  }, [key, attempt]);
  return {
    ...(state.key === key ? state : { key, data: empty, error: false, loading: true }),
    reload: () => setAttempt((n) => n + 1),
  };
}
export function useHotelContentBatch(scope: string, ids: string[], locale: string, transport: HotelSearchTransport) {
  const language = hotelContentLanguage(locale),
    unique = [...new Set(ids)].sort();
  const key = `${scope}:${JSON.stringify(language)}`;
  const cache = useRef<{ key: string; data: Record<string, HotelPublicContent>; seen: Set<string> }>({
    key,
    data: {},
    seen: new Set(),
  });
  if (cache.current.key !== key) cache.current = { key, data: {}, seen: new Set() };
  const [state, setState] = useState({ key, data: cache.current.data, loading: false, error: false });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const abort = new AbortController(),
      holder = cache.current;
    const missing = unique.filter((id) => !holder.seen.has(id));
    if (!missing.length) {
      setState({ key, data: holder.data, loading: false, error: false });
      return;
    }
    setState({ key, data: holder.data, loading: true, error: false });
    const timer = setTimeout(() => {
      abort.abort();
      if (cache.current === holder) setState({ key, data: holder.data, loading: false, error: true });
    }, 15000);
    void readHotelContentBatch(transport, missing, language, abort.signal)
      .then((data) => {
        if (abort.signal.aborted || cache.current !== holder) return;
        holder.data = { ...holder.data, ...data };
        missing.forEach((id) => holder.seen.add(id));
        setState({ key, data: holder.data, loading: false, error: false });
      })
      .catch(() => {
        if (!abort.signal.aborted && cache.current === holder)
          setState({ key, data: holder.data, loading: false, error: true });
      })
      .finally(() => clearTimeout(timer));
    return () => {
      abort.abort();
      clearTimeout(timer);
    };
  }, [key, JSON.stringify(unique), attempt, transport]);
  return {
    ...(state.key === key ? state : { key, data: {}, loading: true, error: false }),
    reload: () => setAttempt((n) => n + 1),
  };
}
export function useHotelContentDetail(scope: string, id: string, locale: string, transport: HotelSearchTransport) {
  const language = hotelContentLanguage(locale);
  return usePublishedContent<HotelPublicContent | null>(
    `${scope}:${id}:${JSON.stringify(language)}`,
    async (signal) => {
      const data = await transport.content(id, language, signal);
      if (data.hotel_id !== id) throw new Error('Content identity mismatch');
      return data;
    },
    null,
  );
}
